/**
 * Cell concentration from a hemocytometer count.
 *
 * Neubauer-improved geometry (DIN ISO 12847, as printed by Marienfeld): a 3 × 3 mm grid of
 * nine 1 mm² squares, chamber depth 0.1 mm. The central 1 mm² square is split into 25
 * group squares of 0.2 × 0.2 mm (0.04 mm²), each holding 16 smallest squares of
 * 0.05 × 0.05 mm (0.0025 mm²).
 *
 * One counted square holds  side² × depth  of liquid; 1 mm³ = 1 µL = 10⁻³ mL, so a 1 mm
 * square at 0.1 mm depth is 10⁻⁴ mL — the origin of the familiar "× 10⁴".
 *
 * cells/mL = mean cells per square × dilution factor ÷ volume of one square (mL).
 */

export type CountRegion = "large" | "group" | "small";

export const REGION_SIDE_MM: Record<CountRegion, number> = {
  large: 1,
  group: 0.2,
  small: 0.05,
};

export const STANDARD_DEPTH_MM = 0.1;
const ML_PER_MM3 = 1e-3;

export function squareVolumeMl(region: CountRegion, depthMm: number): number {
  const side = REGION_SIDE_MM[region];
  return side * side * depthMm * ML_PER_MM3;
}

/** Overall dilution when `cellUl` of suspension is mixed with `dyeUl` of dye or diluent. */
export function dilutionFromVolumes(
  cellUl: number,
  dyeUl: number,
  priorFactor: number
): number | null {
  if (!(cellUl > 0 && dyeUl >= 0 && priorFactor >= 1)) {
    return null;
  }
  return ((cellUl + dyeUl) / cellUl) * priorFactor;
}

export type CountError = "live" | "dead" | "dilution" | "depth" | "squares";

export interface CountInput {
  /** Live (unstained) cells in each counted square. */
  live: readonly number[];
  /** Dead (stained) cells in the same squares, in the same order; null when not counted. */
  dead: readonly number[] | null;
  region: CountRegion;
  depthMm: number;
  dilution: number;
  suspensionMl: number | null;
}

export interface CountResult {
  squares: number;
  totalLive: number;
  meanLive: number;
  sdLive: number | null;
  cvPercent: number | null;
  liveConc: number;
  deadConc: number | null;
  totalConc: number | null;
  viabilityPercent: number | null;
  totalLiveCells: number | null;
  /** Counting error of the total, 1 ÷ √N as a percentage (Poisson). */
  poissonPercent: number | null;
  volumePerSquareMl: number;
}

function sum(values: readonly number[]): number {
  return values.reduce((a, b) => a + b, 0);
}

function sampleSd(values: readonly number[], mean: number): number | null {
  if (values.length < 2) {
    return null;
  }
  const ss = values.reduce((acc, v) => acc + (v - mean) ** 2, 0);
  return Math.sqrt(ss / (values.length - 1));
}

function validCounts(values: readonly number[]): boolean {
  return values.every((v) => Number.isFinite(v) && v >= 0);
}

export function countCells(
  input: CountInput
): { result: CountResult } | { error: CountError } {
  if (input.live.length === 0 || !validCounts(input.live)) {
    return { error: "live" };
  }
  if (input.dead !== null && !validCounts(input.dead)) {
    return { error: "dead" };
  }
  if (input.dead !== null && input.dead.length !== input.live.length) {
    return { error: "squares" };
  }
  if (!(input.dilution >= 1)) {
    return { error: "dilution" };
  }
  if (!(input.depthMm > 0)) {
    return { error: "depth" };
  }
  const squares = input.live.length;
  const volume = squareVolumeMl(input.region, input.depthMm);
  const perMl = input.dilution / volume;
  const totalLive = sum(input.live);
  const meanLive = totalLive / squares;
  const sd = sampleSd(input.live, meanLive);
  const totalDead = input.dead === null ? null : sum(input.dead);
  const liveConc = meanLive * perMl;
  const deadConc = totalDead === null ? null : (totalDead / squares) * perMl;
  const allCells = totalDead === null ? null : totalLive + totalDead;
  return {
    result: {
      squares,
      totalLive,
      meanLive,
      sdLive: sd,
      cvPercent: sd !== null && meanLive > 0 ? (sd / meanLive) * 100 : null,
      liveConc,
      deadConc,
      totalConc: deadConc === null ? null : liveConc + deadConc,
      viabilityPercent:
        allCells !== null && allCells > 0 ? (totalLive / allCells) * 100 : null,
      totalLiveCells:
        input.suspensionMl !== null && input.suspensionMl > 0
          ? liveConc * input.suspensionMl
          : null,
      poissonPercent: totalLive > 0 ? 100 / Math.sqrt(totalLive) : null,
      volumePerSquareMl: volume,
    },
  };
}
